import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { AppShell } from '~/components/layout/AppShell'
import { LoadingRows } from '~/components/primitives/Feedback'
import { CommandPalette } from '~/components/command/CommandPalette'
import { RequireAuth } from '~/features/auth/RequireAuth'
import { isPortalPath } from '~/features/portal/isPortalPath'

// Split per screen: the first paint is the overview, and the sequence editor should not
// be on the critical path to seeing whether the engine is running.
const OverviewPage = lazy(() => import('~/features/overview/OverviewPage'))
const CampaignListPage = lazy(() => import('~/features/campaigns/CampaignListPage'))
const CampaignPage = lazy(() => import('~/features/campaigns/CampaignPage'))
const AccountsPage = lazy(() => import('~/features/accounts/AccountsPage'))
const InboxPage = lazy(() => import('~/features/inbox/InboxPage'))
const TablesPage = lazy(() => import('~/features/prospecting/TablesPage'))
const TablePage = lazy(() => import('~/features/prospecting/TablePage'))
const RecipesPage = lazy(() => import('~/features/prospecting/RecipesPage'))
const RunsPage = lazy(() => import('~/features/prospecting/RunsPage'))
const RunPage = lazy(() => import('~/features/prospecting/RunPage'))
const ApprovalsPage = lazy(() => import('~/features/approvals/ApprovalsPage'))
const ClientsPage = lazy(() => import('~/features/clients/ClientsPage'))
const ClientPage = lazy(() => import('~/features/clients/ClientPage'))
// A client's dashboard (BIBLE §19.5): a separate app under `/c/{slug}`, in the same build.
const PortalApp = lazy(() => import('~/features/portal/PortalApp'))

export function App() {
  const { pathname } = useLocation()
  // Not behind the operator's login, and not inside the console's shell: a client's
  // dashboard has its own sign-in and shows nothing of the console.
  if (isPortalPath(pathname)) {
    return (
      <Suspense fallback={<LoadingRows rows={6} />}>
        <PortalApp />
      </Suspense>
    )
  }
  return (
    <RequireAuth>
      <AppShell>
        <CommandPalette />
        <Suspense fallback={<LoadingRows rows={6} />}>
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/campaigns" element={<CampaignListPage />} />
            <Route path="/campaigns/:campaignId" element={<CampaignPage />} />
            <Route path="/prospecting" element={<TablesPage />} />
            {/* Literal paths before parameterised ones — a literal path must win. */}
            <Route path="/prospecting/recipes" element={<RecipesPage />} />
            <Route path="/prospecting/runs" element={<RunsPage />} />
            <Route path="/prospecting/runs/:pullId" element={<RunPage />} />
            <Route path="/prospecting/tables/:tableId" element={<TablePage />} />
            {/* The run log used to live under /pulls, and bookmarks outlive renames. */}
            <Route path="/prospecting/pulls/:pullId" element={<RunRedirect />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/:conversationId" element={<InboxPage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:clientId" element={<ClientPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppShell>
    </RequireAuth>
  )
}

/** A bookmarked pull, sent to where the run log moved. */
function RunRedirect() {
  const { pullId = '' } = useParams()
  return <Navigate to={`/prospecting/runs/${pullId}`} replace />
}
